import { CodeDisplay } from './components/CodeDisplay.js';
import { generateQRCode } from '../qr.js';
import { getOTPAuthURI, formatSecret } from '../crypto.js';
import { buildPairingURL } from '../utils/payload.js';
import { shareLink, copyToClipboard, showToast, canNativeShare } from '../utils/share.js';
import { isNativeApp } from '../utils/pwa.js';

/**
 * Pairing view showing QR code, live code, and share options
 */
export class PairingView {
  /**
   * @param {Object} options
   * @param {string} options.secret - Base32 encoded TOTP secret
   * @param {string} options.nameA - First person's name
   * @param {string} options.nameB - Second person's name
   */
  constructor({ secret, nameA, nameB }) {
    this.secret = secret;
    this.nameA = nameA;
    this.nameB = nameB;
    this.codeDisplay = null;
    this.element = this.createElement();
    this.init();
  }

  /**
   * Create the view element
   * @returns {HTMLElement}
   */
  createElement() {
    const container = document.createElement('div');
    container.className = 'fade-in';

    const shareUrl = buildPairingURL(this.secret, this.nameA, this.nameB);
    const formattedSecret = formatSecret(this.secret);

    container.innerHTML = `
      <div class="text-center mb-6">
        <a href="#/new" class="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Create new
        </a>
        <h1 class="text-xl font-bold text-slate-800">
          ${this.escapeHtml(this.nameA)} <span class="text-green-600">↔</span> ${this.escapeHtml(this.nameB)}
        </h1>
        <p class="text-sm text-slate-500 mt-1">Shared verification code</p>
      </div>

      <!-- Live Code Display -->
      <div id="code-display-container" class="mb-6"></div>

      <!-- Steps -->
      <div class="space-y-4">
        <!-- Step 1: Add to Authenticator -->
        <div class="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <button class="step-toggle w-full px-4 py-3 flex items-center justify-between text-left" data-step="1">
            <div class="flex items-center">
              <span class="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full text-sm font-bold flex items-center justify-center mr-3">1</span>
              <span class="font-medium text-slate-800">Add to authenticator app</span>
            </div>
            <svg class="w-5 h-5 text-slate-400 step-chevron transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div class="step-content hidden px-4 pb-4" data-step="1">
            <div class="flex flex-col items-center">
              <div id="qr-container" class="qr-container bg-white p-4 rounded-xl border border-slate-100 mb-4"></div>
              <p class="text-sm text-slate-500 text-center mb-3">
                Scan with Google Authenticator, Apple Passwords, 1Password, or any TOTP app
              </p>
              <div class="w-full">
                <label class="block text-xs font-medium text-slate-500 mb-1">Manual entry</label>
                <div class="flex items-center gap-2">
                  <code class="secret-text flex-1 bg-slate-50 px-3 py-2 rounded-lg text-slate-700 border border-slate-100 overflow-x-auto">
                    ${formattedSecret}
                  </code>
                  <button id="copy-secret" class="flex-shrink-0 p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Copy secret">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Share Link -->
        <div class="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <button class="step-toggle w-full px-4 py-3 flex items-center justify-between text-left" data-step="2">
            <div class="flex items-center">
              <span class="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full text-sm font-bold flex items-center justify-center mr-3">2</span>
              <span class="font-medium text-slate-800">Share with ${this.escapeHtml(this.nameB)}</span>
            </div>
            <svg class="w-5 h-5 text-slate-400 step-chevron transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div class="step-content hidden px-4 pb-4" data-step="2">
            <p class="text-sm text-slate-500 mb-3">
              Send this link so they can add the same code to their authenticator
            </p>
            <div class="flex items-center gap-2 mb-3">
              <input
                type="text"
                readonly
                value="${shareUrl}"
                class="flex-1 bg-slate-50 px-3 py-2 rounded-lg text-sm text-slate-600 border border-slate-100 truncate"
              >
              <button id="copy-link" class="flex-shrink-0 p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Copy link">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            ${canNativeShare() ? `
              <button id="native-share" class="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
            ` : ''}
          </div>
        </div>

        <!-- Step 3: Verify -->
        <div class="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <button class="step-toggle w-full px-4 py-3 flex items-center justify-between text-left" data-step="3">
            <div class="flex items-center">
              <span class="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full text-sm font-bold flex items-center justify-center mr-3">3</span>
              <span class="font-medium text-slate-800">Verify codes match</span>
            </div>
            <svg class="w-5 h-5 text-slate-400 step-chevron transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div class="step-content hidden px-4 pb-4" data-step="3">
            <p class="text-sm text-slate-600">
              Once ${this.escapeHtml(this.nameB)} has added the code, call each other and ask:
              <strong class="text-slate-800">"What's our code?"</strong>
            </p>
            <p class="text-sm text-slate-500 mt-2">
              If the 6-digit numbers match, you've verified it's really them! Bookmark this page to verify anytime.
            </p>
          </div>
        </div>
      </div>

      <!-- Tip -->
      <div class="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p class="text-sm font-medium text-amber-800">Security tip</p>
            <p class="text-sm text-amber-700 mt-1">
              Verify codes over a phone call or video chat the first time. This confirms no one intercepted the link.
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-6 text-center">
        <p class="text-xs text-slate-400">
          ${isNativeApp()
            ? 'Your code never leaves your device.'
            : 'The code in this URL never leaves your browser.<br>Bookmark this page to access your verification code anytime.'}
        </p>
      </div>
    `;

    return container;
  }

  /**
   * Initialize the view (attach event handlers, generate QR codes)
   */
  async init() {
    // Initialize code display
    const codeContainer = this.element.querySelector('#code-display-container');
    this.codeDisplay = new CodeDisplay(codeContainer, this.secret);

    // Generate QR code
    const qrContainer = this.element.querySelector('#qr-container');
    const otpUri = getOTPAuthURI(this.secret, this.nameA, this.nameB);
    await generateQRCode(qrContainer, otpUri, { width: 180 });

    // Attach event handlers
    this.attachEventHandlers();

    // Open step 1 by default
    this.toggleStep('1');
  }

  /**
   * Attach event handlers
   */
  attachEventHandlers() {
    const shareUrl = buildPairingURL(this.secret, this.nameA, this.nameB);

    // Step toggles
    this.element.querySelectorAll('.step-toggle').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const step = toggle.dataset.step;
        this.toggleStep(step);
      });
    });

    // Copy secret button
    this.element.querySelector('#copy-secret')?.addEventListener('click', async () => {
      const success = await copyToClipboard(this.secret);
      if (success) {
        showToast('Secret copied!');
      }
    });

    // Copy link button
    this.element.querySelector('#copy-link')?.addEventListener('click', async () => {
      const success = await copyToClipboard(shareUrl);
      if (success) {
        showToast('Link copied!');
      }
    });

    // Native share button
    this.element.querySelector('#native-share')?.addEventListener('click', async () => {
      await shareLink(
        shareUrl,
        `Handshake: ${this.nameA} ↔ ${this.nameB}`,
        `Add this verification code so we can confirm it's really each other`
      );
    });
  }

  /**
   * Toggle a step accordion
   * @param {string} step - Step number
   */
  toggleStep(step) {
    const content = this.element.querySelector(`.step-content[data-step="${step}"]`);
    const toggle = this.element.querySelector(`.step-toggle[data-step="${step}"]`);
    const chevron = toggle?.querySelector('.step-chevron');

    if (content && chevron) {
      const isHidden = content.classList.contains('hidden');
      content.classList.toggle('hidden');
      chevron.style.transform = isHidden ? 'rotate(180deg)' : '';
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.codeDisplay) {
      this.codeDisplay.destroy();
    }
  }
}
